import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useTenantHeader } from "hooks/useTenantHeader";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useStudents, Student } from "../../hooks/useStudents";
import { useAttendance, MonthType } from "../../hooks/useAttendance";
import { useAuth } from "../../hooks/useAuth";
import { useTeacher } from "../../hooks/useTeacher";
import { format, parseISO, isValid } from "date-fns";
import { useHolidays } from "../../hooks/useHolidays";

interface AttendanceFormModalProps {
  open: boolean;
  onClose: () => void;
  initialTeacherId?: number | null;
  initialDate?: string | null; // yyyy-MM-dd (used to derive month/year for edit)
}

type AttendanceCode = "PRESENT" | "ABSENT" | "LEAVE" | "";

const MONTH_OPTIONS: { value: MonthType; labelKey: string }[] = [
  { value: "JANUARY", labelKey: "january" },
  { value: "FEBRUARY", labelKey: "february" },
  { value: "MARCH", labelKey: "march" },
  { value: "APRIL", labelKey: "april" },
  { value: "MAY", labelKey: "may" },
  { value: "JUNE", labelKey: "june" },
  { value: "JULY", labelKey: "july" },
  { value: "AUGUST", labelKey: "august" },
  { value: "SEPTEMBER", labelKey: "september" },
  { value: "OCTOBER", labelKey: "october" },
  { value: "NOVEMBER", labelKey: "november" },
  { value: "DECEMBER", labelKey: "december" },
];

const parseIsoDay = (iso: string): number | null => {
  // expects yyyy-MM-dd
  const parts = iso.split("-");
  if (parts.length !== 3) return null;
  const day = Number(parts[2]);
  return Number.isFinite(day) ? day : null;
};

const AttendanceFormModal: React.FC<AttendanceFormModalProps> = ({
  open,
  onClose,
  initialTeacherId,
  initialDate,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedSession, selectedClass } = useSessionClass();
  const { tenantLoading } = useTenantHeader();

  const schoolId = user?.schoolId;
  const { teachersQuery } = useTeacher(schoolId ?? 0, tenantLoading);
  const { useStudentsBySessionIdAndClassId } = useStudents(tenantLoading);
  const { useholidaysQueryBySession } = useHolidays(selectedSession?.id);
  const { bulkAttendanceMutation, attendanceStatusQuery, getAttendanceQuery } = useAttendance(
    selectedClass?.id && selectedSession?.id
      ? {
          classId: selectedClass.id,
          sessionId: selectedSession.id,
          month: undefined,
          year: undefined,
        }
      : undefined
  );

  const now = new Date();
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | "">("");
  const [month, setMonth] = useState<MonthType>(MONTH_OPTIONS[now.getMonth()]?.value || "JANUARY");
  const [year, setYear] = useState<number>(now.getFullYear());
  const [teacherTouched, setTeacherTouched] = useState(false);

  const studentsQuery = useStudentsBySessionIdAndClassId(selectedSession?.id, selectedClass?.id);

  const students = useMemo(() => {
    const list = studentsQuery.data || [];
    return list
      .filter((s) => s.status !== false)
      .slice()
      .sort((a, b) =>
        (a.studentName || "").toLowerCase().localeCompare((b.studentName || "").toLowerCase())
      );
  }, [studentsQuery.data]);

  const days = useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), []);

  const [attendance, setAttendance] = useState<Record<number, Record<number, AttendanceCode>>>({});

  const isHolidayOrSunday = useMemo(() => {
    const holidays = useholidaysQueryBySession.data || [];
    const classId = selectedClass?.id || 0;
    const mIdx = (() => {
      const list: MonthType[] = [
        "JANUARY",
        "FEBRUARY",
        "MARCH",
        "APRIL",
        "MAY",
        "JUNE",
        "JULY",
        "AUGUST",
        "SEPTEMBER",
        "OCTOBER",
        "NOVEMBER",
        "DECEMBER",
      ];
      return Math.max(0, list.indexOf(month as any));
    })();

    return (day: number) => {
      const d = new Date(year, mIdx, day);
      if (isNaN(d.getTime())) return false;
      // Disallow future dates for attendance marking
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dd = new Date(d);
      dd.setHours(0, 0, 0, 0);
      if (dd > today) return true;
      if (d.getDay() === 0) return true; // Sunday

      const dateStr = format(d, "dd-MM-yyyy");
      return holidays.some((h) => {
        if (!h.dates?.includes(dateStr)) return false;
        if (h.forAllClasses) return true;
        return (h.classIds || []).includes(classId);
      });
    };
  }, [useholidaysQueryBySession.data, selectedClass?.id, month, year]);

  // Fetch attendance for month/year (using existing query function but overriding params)
  const monthAttendanceQuery = useAttendance(
    selectedClass?.id && selectedSession?.id
      ? {
          classId: selectedClass.id,
          sessionId: selectedSession.id,
          month,
          year,
        }
      : undefined
  ).getAttendanceQuery;

  useEffect(() => {
    if (!open) return;

    // When editing, prefer the teacher from the selected record.
    if (!teacherTouched && initialTeacherId) {
      setSelectedTeacherId(Number(initialTeacherId));
      return;
    }

    // Default teacher: match logged-in user -> teacher.userId
    if (!teacherTouched) {
      const userId = user?.id;
      const defaultTeacher = userId ? teachersQuery.data?.find((tt) => tt.userId === userId) : undefined;
      setSelectedTeacherId(defaultTeacher?.id ?? "");
    }
  }, [open, teachersQuery.data, user?.id, initialTeacherId, teacherTouched]);

  useEffect(() => {
    if (!open) return;
    if (!initialDate) return;

    try {
      const d = parseISO(initialDate);
      if (!isValid(d)) return;
      const mIndex = d.getMonth();
      const nextMonth = MONTH_OPTIONS[mIndex]?.value;
      if (nextMonth) setMonth(nextMonth);
      setYear(d.getFullYear());
    } catch {
      // ignore
    }
  }, [open, initialDate]);

  useEffect(() => {
    if (!open) return;
    // Reset grid when month/year changes, then attempt to prefill from fetched data
    const next: Record<number, Record<number, AttendanceCode>> = {};
    for (const s of students) next[s.id!] = {};

    const data = monthAttendanceQuery.data || [];
    const statusIdToCode = new Map<number, AttendanceCode>(
      (attendanceStatusQuery.data || []).map((s) => [s.id, String(s.statusCode).toUpperCase() as AttendanceCode])
    );

    data.forEach((r) => {
      const studentId = r.studentId;
      if (!studentId) return;
      if (!next[studentId]) next[studentId] = {};

      // New API shape: dayStatusMap { "1": 1, "2": null, ... }
      if (r.dayStatusMap && typeof r.dayStatusMap === "object") {
        Object.entries(r.dayStatusMap as Record<string, number | null>).forEach(([dayStr, statusId]) => {
          const day = Number(dayStr);
          if (!Number.isFinite(day) || day < 1 || day > 31) return;
          if (statusId == null) return;
          const code = statusIdToCode.get(Number(statusId)) || "";
          if (!code) return;
          next[studentId][day] = code;
        });
        return;
      }

      // Legacy fallback: attendanceDate + statusCode
      const d = parseIsoDay(r.attendanceDate);
      if (!d) return;
      const code = (r.statusCode as AttendanceCode) || "";
      next[studentId][d] = code;
    });

    setAttendance(next);
  }, [open, month, year, students, monthAttendanceQuery.data, attendanceStatusQuery.data]);

  const handleTeacherChange = (e: SelectChangeEvent) => {
    const v = e.target.value;
    setTeacherTouched(true);
    setSelectedTeacherId(v === "" ? "" : Number(v));
  };

  const handleMonthChange = (e: SelectChangeEvent) => {
    setMonth(e.target.value as MonthType);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    if (!Number.isFinite(v)) return;
    setYear(v);
  };

  const setCell = (studentId: number, day: number, code: AttendanceCode) => {
    if (isHolidayOrSunday(day)) return;
    setAttendance((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        [day]: code,
      },
    }));
  };

  const handleSubmit = () => {
    if (!selectedSession?.id || !selectedClass?.id) return;
    if (!selectedTeacherId) return;

    const statusMap = new Map(
      (attendanceStatusQuery.data || []).map((s) => [String(s.statusCode).toUpperCase(), s.id])
    );

    const items: { studentId: number; day: number; statusId: number }[] = [];
    Object.entries(attendance).forEach(([sid, daysMap]) => {
      const studentId = Number(sid);
      Object.entries(daysMap || {}).forEach(([d, code]) => {
        if (!code) return;
        const day = Number(d);
        if (isHolidayOrSunday(day)) return;
        const statusId = statusMap.get(code.toUpperCase()) || 0;
        if (!statusId) return;
        items.push({ studentId, day, statusId });
      });
    });

    bulkAttendanceMutation.mutate(
      {
        staffId: Number(selectedTeacherId),
        classId: selectedClass.id,
        sessionId: selectedSession.id,
        month,
        year,
        students: items,
      },
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <Dialog open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      maxWidth="xl"
      fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {t("take_attendance")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, overflow: "visible" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            mb: 2,
            overflow: "visible",
          }}
        >
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="caption" color="text.secondary">{t("session")}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {selectedSession?.name || t("n/a")}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="caption" color="text.secondary">{t("class")}</Typography>
            <Typography variant="body2" fontWeight={600}>
              {selectedClass?.name || t("n/a")}
            </Typography>
          </Box>

          <FormControl
            size="small"
            sx={{
              minWidth: 240,
              "& .MuiInputBase-root": { height: 44 },
              "& .MuiSelect-select": { display: "flex", alignItems: "center" },
              pt: 0.25,
            }}
          >
            <InputLabel shrink>{t("teacher_name")}</InputLabel>
            <Select
              label={t("teacher_name")}
              value={selectedTeacherId === "" ? "" : String(selectedTeacherId)}
              onChange={handleTeacherChange}
            >
              <MenuItem value="">{t("select")}</MenuItem>
              {teachersQuery.data?.map((teacher) => (
                <MenuItem key={teacher.id} value={String(teacher.id)}>
                  {teacher.firstName} {teacher.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiInputBase-root": { height: 44 },
              "& .MuiSelect-select": { display: "flex", alignItems: "center" },
              pt: 0.25,
            }}
          >
            <InputLabel shrink>{t("month")}</InputLabel>
            <Select label={t("month")} value={month} onChange={handleMonthChange}>
              {MONTH_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {t(m.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              minWidth: 140,
              "& .MuiInputBase-root": { height: 44 },
              "& .MuiSelect-select": { display: "flex", alignItems: "center" },
              pt: 0.25,
            }}
          >
            <InputLabel shrink>{t("year")}</InputLabel>
            <Select
              label={t("year")}
              value={String(year)}
              onChange={(e) => handleYearChange({ target: { value: e.target.value } } as any)}
            >
              {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i).map((y) => (
                <MenuItem key={y} value={String(y)}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ minHeight: 520 }}>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <TableContainer
              sx={{
                maxHeight: 620,
                overflowX: "auto",
                overflowY: "auto",
                "&::-webkit-scrollbar": { height: 10, width: 10 },
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1550,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        bgcolor: "#f5f7fb",
                        fontWeight: 800,
                        minWidth: 240,
                        borderRight: "1px solid",
                        borderColor: "divider",
                        borderBottom: "1px solid",
                      }}
                    >
                      {t("student")}
                    </TableCell>
                    {days.map((d) => (
                      <TableCell
                        key={d}
                        align="center"
                        sx={{
                          bgcolor: "#f5f7fb",
                          fontWeight: 800,
                          width: 78,
                          borderLeft: "1px solid",
                          borderColor: "divider",
                          borderBottom: "1px solid",
                        }}
                      >
                        {d}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 2,
                          bgcolor: "background.paper",
                          borderRight: "1px solid",
                          borderColor: "divider",
                          minWidth: 240,
                          borderBottom: "1px solid",
                        }}
                      >
                        <Typography variant="body2" fontWeight={700}>
                          {s.studentName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {s.enrolmentNo || ""}
                        </Typography>
                      </TableCell>
                      {days.map((d) => {
                        const value = attendance[s.id!]?.[d] || "";
                    const disabledDay = isHolidayOrSunday(d);
                        return (
                          <TableCell
                            key={d}
                            align="center"
                            sx={{
                              px: 0.75,
                              py: 0.75,
                              borderLeft: "1px solid",
                              borderBottom: "1px solid",
                              borderColor: "divider",
                              bgcolor: disabledDay ? "rgba(244, 67, 54, 0.03)" : "transparent",
                            }}
                          >
                            <ToggleButtonGroup
                              exclusive
                              size="small"
                              value={value}
                          onChange={(_e, v) => setCell(s.id!, d, (v || "") as AttendanceCode)}
                          disabled={disabledDay}
                              sx={{
                                "& .MuiToggleButton-root": {
                                  px: 0.75,
                                  py: 0.25,
                                  minWidth: 0,
                                  borderRadius: 1,
                                  fontWeight: 800,
                                  fontSize: 11,
                                },
                                gap: 0.75,
                                "& .MuiToggleButtonGroup-grouped": {
                                  margin: 0,
                                  border: "1px solid",
                                  borderColor: "divider",
                                },
                              }}
                            >
                              <ToggleButton
                                value="PRESENT"
                                sx={{
                                  color: "success.main",
                                  borderColor: "success.light",
                                  "&.Mui-selected": { bgcolor: "success.main", color: "common.white" },
                                  "&.Mui-selected:hover": { bgcolor: "success.dark" },
                                }}
                              >
                                P
                              </ToggleButton>
                              <ToggleButton
                                value="ABSENT"
                                sx={{
                                  color: "error.main",
                                  borderColor: "error.light",
                                  "&.Mui-selected": { bgcolor: "error.main", color: "common.white" },
                                  "&.Mui-selected:hover": { bgcolor: "error.dark" },
                                }}
                              >
                                A
                              </ToggleButton>
                              <ToggleButton
                                value="LEAVE"
                                sx={{
                                  color: "warning.main",
                                  borderColor: "warning.light",
                                  "&.Mui-selected": { bgcolor: "warning.main", color: "common.white" },
                                  "&.Mui-selected:hover": { bgcolor: "warning.dark" },
                                }}
                              >
                                L
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: "divider", bgcolor: "#fcfcfc" }}>
              <Typography variant="caption" color="text.secondary">
                {t("legend")}:{" "}
                <Box component="span" sx={{ color: "success.main", fontWeight: 800 }}>P</Box> {t("present")},{" "}
                <Box component="span" sx={{ color: "error.main", fontWeight: 800 }}>A</Box> {t("absent")},{" "}
                <Box component="span" sx={{ color: "warning.main", fontWeight: 800 }}>L</Box> {t("leave")}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderColor: "#3092e3", color: "#3092e3" }}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#3092e3",
            "&:hover": { backgroundColor: "primary.main" },
          }}
          onClick={handleSubmit}
          disabled={
            !selectedTeacherId ||
            bulkAttendanceMutation.isLoading ||
            attendanceStatusQuery.isLoading ||
            students.length === 0
          }
        >
          {bulkAttendanceMutation.isLoading ? <CircularProgress size={22} sx={{ color: "common.white" }} /> : t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceFormModal;